import '@vitejs/plugin-react/preamble'
import { initializeEditorNavigation } from '@/lib/editor-navigation'
import { InertiaI18nProvider } from '@/lib/i18n'
import { documentStyleNonce } from '@/lib/vendor-style-nonce'
import { colors } from '@/styles/tokens.stylex'
import '@/styles/development'
import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import type { SharedProps } from '@/types/session'


const pages = import.meta.glob<{ default: ResolvedComponent }>([
  '../pages/**/*.tsx',
  '!../pages/**/*.test.tsx',
  '!../pages/**/*.spec.tsx',
])
const inertiaRootId = "app"

function ensureInertiaRoot() {
  if (typeof document === "undefined") return

  if (document.getElementById(inertiaRootId)) return

  const initialPage = document.querySelector<HTMLScriptElement>(
    `script[data-page="${inertiaRootId}"][type="application/json"]`
  )

  if (!initialPage) return

  const root = document.createElement("div")
  root.id = inertiaRootId
  initialPage.insertAdjacentElement("afterend", root)
}

ensureInertiaRoot()
const disposeEditorNavigation = initializeEditorNavigation()
import.meta.hot?.dispose(disposeEditorNavigation)

void createInertiaApp({
  nonce: documentStyleNonce(document),
  progress: { color: colors['--rel-brand'] },
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: async (name) => {
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page: ReactNode) => (<Layout>{page}</Layout>)

    return (await page()).default
  },

  setup({ el, App, props }) {
    createRoot(el).render(
      <StrictMode>
        <App {...props}>
          {({ Component, key, props: pageProps }) => (
            <InertiaI18nProvider page={pageProps as SharedProps}>
              <Component key={key} {...pageProps} />
            </InertiaI18nProvider>
          )}
        </App>
      </StrictMode>
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
